// High-score table persisted in localStorage (per browser). Capped at
// table_size; qualifies when the table isn't full or the score beats the lowest.

export interface HighScore {
  name: string;
  score: number;
  date: string;
}

const KEY = "potd_highscores";

// Seeded placeholder leaderboard so the table reads as real on first run.
const SEED: HighScore[] = [
  { name: "WILIK", score: 5000, date: "" },
  { name: "OLIVER", score: 4000, date: "" },
  { name: "MARINA", score: 3000, date: "" },
  { name: "TANA", score: 2000, date: "" },
  { name: "IVETA", score: 1000, date: "" },
];

let recentKey = ""; // identifies the most-recently inserted entry (for highlight)

function keyOf(e: HighScore): string {
  return `${e.name}:${e.score}:${e.date}`;
}

function sanitize(arr: unknown): HighScore[] {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((e) => ({
      name: String((e as HighScore)?.name ?? "").slice(0, 8),
      score: Number((e as HighScore)?.score) || 0,
      date: String((e as HighScore)?.date ?? ""),
    }))
    .filter((e) => Number.isFinite(e.score))
    .sort((a, b) => b.score - a.score);
}

export function loadScores(tableSize: number): HighScore[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const arr = sanitize(JSON.parse(raw));
      if (arr.length > 0) return arr.slice(0, tableSize);
    }
  } catch {
    /* fall through to seed */
  }
  return SEED.slice(0, tableSize);
}

export function qualifies(score: number, tableSize: number): boolean {
  if (score <= 0) return false;
  const scores = loadScores(tableSize);
  return scores.length < tableSize || score > scores[scores.length - 1].score;
}

// Insert a score, persist, and return the new capped table.
export function insertScore(name: string, score: number, tableSize: number): HighScore[] {
  const date = new Date().toISOString().slice(0, 10);
  const entry: HighScore = { name: name.trim() || "???", score, date };
  const scores = loadScores(tableSize);
  scores.push(entry);
  scores.sort((a, b) => b.score - a.score);
  const capped = scores.slice(0, tableSize);
  recentKey = keyOf(entry);
  try {
    localStorage.setItem(KEY, JSON.stringify(capped));
  } catch {
    /* storage unavailable; scores just won't persist */
  }
  return capped;
}

// Key of the last inserted entry, for highlighting it on the menu.
export function recentEntryKey(e: HighScore): boolean {
  return recentKey !== "" && keyOf(e) === recentKey;
}

// ---------- Shared online leaderboard ----------
// Backed by scores.php (same dir as index.html). localStorage stays as a synchronous
// cache + offline fallback; these async calls reconcile it with the server file on disk.
const SCORES_URL = "scores.php";

function cache(scores: HighScore[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(scores));
  } catch {
    /* storage unavailable */
  }
}

// Fetch the authoritative table from the server and refresh the local cache.
// Returns null on any failure (offline / no backend) so callers keep the cached view.
export async function syncFromServer(tableSize: number): Promise<HighScore[] | null> {
  try {
    const res = await fetch(SCORES_URL, { cache: "no-store" });
    if (!res.ok) return null;
    const arr = sanitize(await res.json()).slice(0, tableSize);
    if (arr.length > 0) {
      cache(arr);
      return arr;
    }
  } catch {
    /* offline or endpoint absent (e.g. local dev): keep the cache */
  }
  return null;
}

// Submit a score to the shared table. Optimistically inserts locally first (also the
// offline fallback + sets the highlight), then POSTs and adopts the server's table.
export async function submitScore(name: string, score: number, tableSize: number): Promise<HighScore[]> {
  const local = insertScore(name, score, tableSize);
  try {
    const res = await fetch(SCORES_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, score }),
    });
    if (res.ok) {
      const arr = sanitize(await res.json()).slice(0, tableSize);
      if (arr.length > 0) {
        cache(arr);
        return arr;
      }
    }
  } catch {
    /* offline: the optimistic local insert stands */
  }
  return local;
}
